import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Upload, Trash2, Image as ImageIcon, Video, Copy, ExternalLink, FolderPlus, Folder, FolderOpen, Edit2, MoreVertical } from 'lucide-react';
import { Toast } from '../Toast';
import { CreateFolderModal } from '../components/CreateFolderModal';
import { RenameFolderModal } from '../components/RenameFolderModal';
import { MoveFolderModal } from '../components/MoveFolderModal';
import { ConfirmModal } from '../ConfirmModal';

interface MediaFile {
    id: number;
    name: string;
    url: string;
    type: 'image' | 'video';
    folder_id?: number | null;
}

interface MediaFolder {
    id: number;
    name: string;
    media_count?: number;
}

export const MediaLib: React.FC = () => {
    const [media, setMedia] = useState<MediaFile[]>([]);
    const [folders, setFolders] = useState<MediaFolder[]>([]);
    const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);

    // Modals
    const [showCreateFolder, setShowCreateFolder] = useState(false);
    const [renamingFolder, setRenamingFolder] = useState<MediaFolder | null>(null);
    const [movingMedia, setMovingMedia] = useState<MediaFile | null>(null);
    const [deletingFolder, setDeletingFolder] = useState<MediaFolder | null>(null);

    useEffect(() => {
        fetchMedia();
        fetchFolders();
    }, []);

    const fetchMedia = async () => {
        setLoading(true);
        try {
            const res = await api.get('/media');
            setMedia(res.data);
        } catch (e) {
            console.error("Erro loading media", e);
        } finally {
            setLoading(false);
        }
    };

    const fetchFolders = async () => {
        try {
            const res = await api.get('/media-folders');
            setFolders(res.data);
        } catch (e) {
            console.error("Erro loading folders", e);
        }
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;

        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('file', file);

        setUploading(true);
        try {
            await api.post('/media/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setToast({ msg: "Arquivo enviado com sucesso!", type: 'success' });
            fetchMedia();
            fetchFolders(); // Atualizar contadores
        } catch (e: any) {
            const errorMsg = e.response?.data?.error || "Erro no upload.";
            setToast({ msg: errorMsg, type: 'error' });
            console.error('Erro no upload:', e);
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (mediaId: number) => {
        if (!window.confirm("Tem certeza que deseja apagar esta mídia?")) return;
        try {
            await api.delete(`/media/${mediaId}`);
            setMedia(media.filter(m => m.id !== mediaId));
            setToast({ msg: "Mídia deletada.", type: 'success' });
            fetchFolders(); // Atualizar contadores
        } catch (e) {
            setToast({ msg: "Erro ao deletar.", type: 'error' });
        }
    };

    const copyToClipboard = (url: string) => {
        navigator.clipboard.writeText(url);
        setToast({ msg: "Link copiado!", type: 'success' });
    };

    // Folder Management
    const handleCreateFolder = async (name: string) => {
        await api.post('/media-folders', {
            account_id: 1,
            name
        });
        setToast({ msg: "Pasta criada com sucesso!", type: 'success' });
        fetchFolders();
    };

    const handleRenameFolder = async (newName: string) => {
        if (!renamingFolder) return;
        await api.put(`/media-folders/${renamingFolder.id}`, {
            name: newName
        });
        setToast({ msg: "Pasta renomeada com sucesso!", type: 'success' });
        fetchFolders();
        setRenamingFolder(null);
    };

    const handleDeleteFolder = async () => {
        if (!deletingFolder) return;
        try {
            await api.delete(`/media-folders/${deletingFolder.id}`);
            setToast({ msg: "Pasta deletada. Mídias movidas para raiz.", type: 'success' });
            fetchFolders();
            fetchMedia();
            setSelectedFolderId(null);
        } catch (e) {
            setToast({ msg: "Erro ao deletar pasta.", type: 'error' });
        } finally {
            setDeletingFolder(null);
        }
    };

    const handleMoveMedia = async (folderId: number | null) => {
        if (!movingMedia) return;
        await api.put(`/media/${movingMedia.id}/move`, {
            folder_id: folderId
        });
        setToast({ msg: "Mídia movida com sucesso!", type: 'success' });
        fetchMedia();
        fetchFolders();
        setMovingMedia(null);
    };

    // Filter media by selected folder
    const filteredMedia = selectedFolderId === null
        ? media.filter(m => !m.folder_id)
        : media.filter(m => m.folder_id === selectedFolderId);

    // Count total media in root
    const rootMediaCount = media.filter(m => !m.folder_id).length;

    return (
        <div className="media-lib-page">
            {toast && <Toast message={toast.msg} type={toast.type} onClose={() => setToast(null)} />}

            {/* Modals */}
            <CreateFolderModal
                isOpen={showCreateFolder}
                onClose={() => setShowCreateFolder(false)}
                onSubmit={handleCreateFolder}
            />

            {renamingFolder && (
                <RenameFolderModal
                    isOpen={!!renamingFolder}
                    onClose={() => setRenamingFolder(null)}
                    currentName={renamingFolder.name}
                    onSubmit={handleRenameFolder}
                />
            )}

            {movingMedia && (
                <MoveFolderModal
                    isOpen={!!movingMedia}
                    onClose={() => setMovingMedia(null)}
                    folders={folders}
                    currentFolderId={movingMedia.folder_id || null}
                    onSubmit={handleMoveMedia}
                />
            )}

            {deletingFolder && (
                <ConfirmModal
                    isOpen={!!deletingFolder}
                    title="Deletar Pasta"
                    message={`Tem certeza que deseja deletar a pasta "${deletingFolder.name}"? As mídias serão movidas para "Todas as Mídias".`}
                    onConfirm={handleDeleteFolder}
                    onCancel={() => setDeletingFolder(null)}
                />
            )}

            {/* Sidebar de Pastas */}
            <aside className="media-lib-sidebar">
                <div className="media-lib-sidebar-header">
                    <h3 className="media-lib-sidebar-title">Pastas</h3>
                </div>

                <div className="media-lib-folder-list">
                    {/* Todas as Mídias (Raiz) */}
                    <button
                        type="button"
                        onClick={() => setSelectedFolderId(null)}
                        className={`media-lib-folder-item ${selectedFolderId === null ? 'active' : ''}`}
                    >
                        <FolderOpen size={18} color={selectedFolderId === null ? '#ff6a00' : '#888'} />
                        <span className="media-lib-folder-name">Todas as Mídias</span>
                        <span className="media-lib-folder-count">{rootMediaCount}</span>
                    </button>

                    {/* Lista de Pastas */}
                    {folders.map(folder => (
                        <div
                            key={folder.id}
                            role="button"
                            tabIndex={0}
                            className={`media-lib-folder-item ${selectedFolderId === folder.id ? 'active' : ''}`}
                            onClick={() => setSelectedFolderId(folder.id)}
                            onKeyDown={(e) => e.key === 'Enter' && setSelectedFolderId(folder.id)}
                        >
                            <Folder size={18} color={selectedFolderId === folder.id ? '#ff6a00' : '#888'} />
                            <span className="media-lib-folder-name">{folder.name}</span>
                            <span className="media-lib-folder-count">{folder.media_count || 0}</span>
                            <div className="media-lib-folder-actions" onClick={(e) => e.stopPropagation()}>
                                <button type="button" onClick={() => setRenamingFolder(folder)} title="Renomear" aria-label="Renomear">
                                    <Edit2 size={12} color="#888" />
                                </button>
                                <button type="button" onClick={() => setDeletingFolder(folder)} title="Deletar" aria-label="Deletar">
                                    <Trash2 size={12} color="#ef4444" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </aside>

            {/* Área Principal */}
            <main className="media-lib-main dashboard-content">
                <div className="top-bar">
                    <h1 className="page-title">
                        {selectedFolderId === null
                            ? 'Todas as Mídias'
                            : folders.find(f => f.id === selectedFolderId)?.name || 'Galeria de Mídias'}
                    </h1>
                    <div className="actions">
                        <label className="glass-button-style">
                            {uploading ? <div className="spinner-sm" style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}></div> : <Upload size={18} />}
                            <span>Fazer Upload</span>
                            <input type="file" hidden onChange={handleUpload} accept="image/*,video/*" />
                        </label>
                    </div>
                </div>

                {loading && media.length === 0 ? (
                    null
                ) : filteredMedia.length === 0 ? (
                    <div className="empty-state-pro">
                        <div className="empty-icon-box">
                            <ImageIcon size={32} />
                        </div>
                        <h3>Sem mídias nesta pasta</h3>
                        <p>Faça upload de imagens ou vídeos ou mova mídias existentes para cá.</p>
                    </div>
                ) : (
                    <div className="media-lib-grid media-grid">
                        {filteredMedia.map((file) => (
                            <div key={file.id} className="media-card" style={{
                                background: '#1e2030',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: '1px solid #ffffff10',
                                position: 'relative'
                            }}>
                                <div className="media-preview" style={{ height: '150px', background: '#000', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {file.type === 'image' ? (
                                        <img src={file.url} alt={file.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    ) : (
                                        <Video size={32} color="#666" />
                                    )}
                                </div>

                                <div className="media-info" style={{ padding: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                                        <button
                                            className="action-icon"
                                            onClick={() => setMovingMedia(file)}
                                            title="Mover para Pasta"
                                            style={{
                                                background: 'rgba(255, 106, 0, 0.1)',
                                                border: '1px solid rgba(255, 106, 0, 0.3)',
                                                borderRadius: '6px',
                                                padding: '6px',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <MoreVertical size={14} color="#ff6a00" />
                                        </button>
                                        <button className="action-icon" onClick={() => copyToClipboard(file.url)} title="Copiar Link">
                                            <Copy size={16} />
                                        </button>
                                        <a href={file.url} target="_blank" rel="noreferrer" className="action-icon" title="Abrir">
                                            <ExternalLink size={16} />
                                        </a>
                                        <button className="action-icon delete" onClick={() => handleDelete(file.id)} title="Excluir">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};
